import React, {
  ReactNode, FC, createRef, useMemo, useRef, useState, useCallback, useEffect, HTMLAttributes, ReactElement, RefObject,
} from 'react';

import { TooltipOffset } from 'ia-react-core/~/interfaces/TooltipOffset';
import Tooltip, { TooltipRenderProps } from '../../tips/Tooltip';

import useMenuIds from './hooks/_useMenuIds';
import MenuListItem from './MenuListItem';
import MenuTarget from './MenuTarget';
import { StyledMenuWrapper, StyledList } from './Menu.styles';

export interface MenuProps extends HTMLAttributes<HTMLDivElement>{
  /** The content of the menu button. */
  children: ReactElement<{
    children?: ReactNode;
    disabled?: boolean;
    'data-testid'?: string;
    ref?: (current: HTMLElement) => void;
  } & HTMLAttributes<HTMLElement>>;
  /** Array of items. */
  items?: ReactElement[];
  /** Disables the menu. */
  disabled?: boolean;
  /** Horizontal offset of the popover. */
  horizontalOffset?: number;
  /** Vertical offset of the popover. */
  verticalOffset?: number;
}

interface MenuInternalProps {
  menuId?: number;
}
interface MenuComposedProps extends MenuProps, MenuInternalProps {}

const MenuDefaultProps: Partial<MenuProps> = {
  items: [],
  disabled: false,
  horizontalOffset: 0,
};

const Menu: FC<MenuComposedProps> = ({
  children, menuId, items, disabled, horizontalOffset, verticalOffset, ...props
}) => {
  const { menuContainerId } = useMenuIds({ menuId });
  const targetRef = useRef<HTMLElement>();
  const [offset, setOffset] = useState<TooltipOffset>([0, 0]);
  const [selectedItem, setSelectedItem] = useState<ReactElement>(null);
  const [itemsRefs, setItemsRefs] = useState<RefObject<HTMLLIElement>[]>([]);

  const selectedItemIndex = useMemo(() => (
    items.indexOf(selectedItem)
  ), [items, selectedItem]);

  const resetSelectedItem = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const updateRef = useCallback((element: HTMLElement) => {
    targetRef.current = element;
    setOffset([horizontalOffset, verticalOffset || (targetRef?.current?.clientHeight || 0) / 2]);
  }, [horizontalOffset, verticalOffset]);

  useEffect(() => {
    // add or remove refs
    setItemsRefs((latestItemsRefs) => (
      items.map((_, index) => latestItemsRefs[index] || createRef())
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const tip = useCallback(({ toggleTooltip }: TooltipRenderProps) => (
    <StyledList>
      {items.map((item, index) => (
        <MenuListItem
          key={index}
          item={item}
          index={index}
          isLast={index === items.length - 1}
          selectedItemIndex={selectedItemIndex}
          onItemClick={() => setTimeout(() => toggleTooltip(false), 100)}
          ref={itemsRefs[index]}
        />
      ))}
    </StyledList>
  ), [items, itemsRefs, selectedItemIndex]);

  return (
    <StyledMenuWrapper>
      <Tooltip
        id={menuContainerId}
        role="menu"
        placement="bottom"
        offset={offset}
        tooltipPadding={0}
        onClose={resetSelectedItem}
        tip={tip}
      >
        <MenuTarget
          {...props}
          items={items}
          selectedItem={selectedItem}
          selectedItemIndex={selectedItemIndex}
          menuContainerId={menuContainerId}
          target={children}
          disabled={disabled}
          updateRef={updateRef}
          setSelectedItem={setSelectedItem}
          itemsRefs={itemsRefs}
          targetRef={targetRef}
        />
      </Tooltip>
    </StyledMenuWrapper>
  );
};

Menu.defaultProps = MenuDefaultProps;

export default Menu;
